/**
 * CrearProyectoModal — Modal para crear proyecto / campaña / evento
 *
 * Definición: iniciativa finita con cliente + fecha_inicio + fecha_fin + budget + brief.
 * Diferente de TAREAS (unidades de trabajo) y GRUPOS (organización mensual).
 *
 * Se abre con evento "crew-action" → "new-proyecto".
 * Pre-llena cliente del ClientContext.
 */
import { useState, useEffect } from "react";
import {
  X, FolderOpen, Building2, CalendarDays, DollarSign, FileText,
  Loader2, AlertCircle, CheckCircle2, Sparkles, Hash,
} from "../lib/icons.jsx";
import { api } from "../api/client.js";
import { cn } from "../lib/utils.js";
import { Button } from "./ui/button.jsx";
import { Input } from "./ui/input.jsx";
import { useClient } from "../context/ClientContext.jsx";
import { useToast } from "./Toast.jsx";

const TIPOS = [
  { value: "campaña",      label: "Campaña" },
  { value: "evento",       label: "Evento" },
  { value: "audiovisual",  label: "Audiovisual" },
  { value: "diseño",       label: "Pack de Diseño" },
  { value: "web",          label: "Web / Desarrollo" },
  { value: "consultoria",  label: "Consultoría" },
  { value: "otro",         label: "Otro" },
];

export function CrearProyectoModal() {
  const { selectedId, clientes } = useClient();
  const { toast } = useToast();

  const [open,   setOpen]   = useState(false);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);
  const [form,   setForm]   = useState(() => emptyForm(selectedId));

  function emptyForm(clienteId) {
    return {
      nombre:           "",
      cliente_id:       clienteId || "",
      tipo:             "campaña",
      estado:           "activo",
      fecha_inicio:     new Date(Date.now() - 3 * 3600 * 1000).toISOString().slice(0, 10),
      fecha_fin:        "",
      budget:           "",
      horas_cotizadas:  "",
      responsable_id:   "",
      brief_doc_url:    "",
      descripcion:      "",
      crearGrupoAsociado: true,  // por defecto crea un grupo CAMPAÑA/EVENTO en GRUPOS
    };
  }

  useEffect(() => {
    const handler = (e) => {
      if (e.detail === "new-proyecto") {
        setForm(emptyForm(selectedId));
        setError(null);
        setOpen(true);
      }
    };
    window.addEventListener("crew-action", handler);
    return () => window.removeEventListener("crew-action", handler);
  }, [selectedId]);

  function setField(k, v) { setForm(f => ({ ...f, [k]: v })); }
  function handleClose() { if (!saving) { setOpen(false); setError(null); } }

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (!form.nombre.trim()) { setError("Nombre es obligatorio"); return; }
    if (!form.cliente_id)    { setError("Cliente es obligatorio"); return; }

    setSaving(true);
    setError(null);
    const toastId = toast.loading("Creando proyecto…");

    try {
      const payload = {
        ...form,
        budget:          form.budget ? Number(form.budget) : null,
        horas_cotizadas: form.horas_cotizadas ? Number(form.horas_cotizadas) : null,
      };
      const res = await api.createProyecto(payload);

      toast.update(toastId, "success", "Proyecto creado", {
        description: res.id,
      });
      window.dispatchEvent(new CustomEvent("crew-refresh", { detail: "proyectos" }));
      setOpen(false);
    } catch (e) {
      setError(e.message);
      toast.update(toastId, "error", "Error", { description: e.message });
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  const clienteSelected = clientes.find(c => c.id === form.cliente_id);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" onClick={handleClose} />

      <div className="relative z-10 w-full max-w-xl max-h-[90vh] overflow-y-auto bg-white border border-border rounded-2xl shadow-2xl shadow-[#CDD0D7]/50">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-muted/50">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#EDE9FE] ring-1 ring-[#C4B5FD] flex items-center justify-center">
              <FolderOpen className="h-4 w-4 text-[#5B21B6]" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-foreground">Nuevo Proyecto</h2>
              <p className="text-[12px] text-muted-foreground">
                {clienteSelected ? `Para: ${clienteSelected.nombre}` : "Iniciativa finita: campaña, evento, audiovisual…"}
              </p>
            </div>
          </div>
          <button onClick={handleClose} disabled={saving} className="p-1.5 rounded-lg hover:bg-muted/60 text-muted-foreground disabled:opacity-30">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <FormField label="Nombre del proyecto" required icon={Hash}>
            <Input
              value={form.nombre}
              onChange={e => setField("nombre", e.target.value)}
              placeholder="ej: Campaña Aniversario 2026"
              autoFocus
            />
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Cliente" required icon={Building2}>
              <select
                value={form.cliente_id}
                onChange={e => setField("cliente_id", e.target.value)}
                className={selectCls}
              >
                <option value="">Seleccionar…</option>
                {clientes
                  .filter(c => c.estado === "activo")
                  .sort((a, b) => a.nombre.localeCompare(b.nombre))
                  .map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </FormField>

            <FormField label="Tipo" icon={FolderOpen}>
              <select
                value={form.tipo}
                onChange={e => setField("tipo", e.target.value)}
                className={selectCls}
              >
                {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Fecha inicio" required icon={CalendarDays}>
              <input
                type="date"
                value={form.fecha_inicio}
                onChange={e => setField("fecha_inicio", e.target.value)}
                className={selectCls}
              />
            </FormField>
            <FormField label="Fecha fin (estimada)" icon={CalendarDays}>
              <input
                type="date"
                value={form.fecha_fin}
                onChange={e => setField("fecha_fin", e.target.value)}
                className={selectCls}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Budget USD" icon={DollarSign}>
              <Input
                type="number"
                step="0.01"
                value={form.budget}
                onChange={e => setField("budget", e.target.value)}
                placeholder="0"
              />
            </FormField>
            <FormField label="Horas cotizadas" icon={Hash}>
              <Input
                type="number"
                step="0.5"
                value={form.horas_cotizadas}
                onChange={e => setField("horas_cotizadas", e.target.value)}
                placeholder="0"
              />
            </FormField>
          </div>

          <FormField label="Brief / Propuesta (Google Doc URL)" icon={FileText}>
            <Input
              type="url"
              value={form.brief_doc_url}
              onChange={e => setField("brief_doc_url", e.target.value)}
              placeholder="https://docs.google.com/document/d/..."
            />
          </FormField>

          <FormField label="Descripción breve" icon={Hash}>
            <textarea
              rows={2}
              value={form.descripcion}
              onChange={e => setField("descripcion", e.target.value)}
              placeholder="Objetivo del proyecto, contexto…"
              className={cn(selectCls, "resize-none py-2")}
            />
          </FormField>

          {/* Crear grupo asociado toggle */}
          <div className="bg-[#F5F3FF] border border-[#DDD6FE] rounded-lg p-3 flex items-start gap-3">
            <input
              id="crear-grupo"
              type="checkbox"
              checked={form.crearGrupoAsociado}
              onChange={e => setField("crearGrupoAsociado", e.target.checked)}
              className="mt-0.5"
            />
            <label htmlFor="crear-grupo" className="flex-1 cursor-pointer">
              <div className="flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-[#5B21B6]" />
                <span className="text-xs font-semibold text-foreground">Crear Grupo asociado en Centro de Operaciones</span>
              </div>
              <p className="text-[12px] text-muted-foreground mt-0.5">
                Para que las tareas del proyecto se agrupen automáticamente bajo el nombre del proyecto.
              </p>
            </label>
          </div>

          {error && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[#FEF2F2] border border-[#FECACA] text-[#B91C1C] text-xs">
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          <div className="flex items-center gap-2 pt-2 border-t border-border/40">
            <Button variant="ghost" type="button" onClick={handleClose} disabled={saving}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="ml-auto gap-1.5">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              {saving ? "Creando…" : "Crear proyecto"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FormField({ label, icon: Icon, children, required }) {
  return (
    <div>
      <label className="flex items-center gap-1 text-[12px] font-medium text-muted-foreground/70 mb-1">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
        {required && <span className="text-[#DC2626] ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const selectCls = "w-full h-9 px-3 rounded-lg bg-white border border-border text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors";
